/**
 * Real-Time WHO Outbreak Monitor
 * Tracks WHO Disease Outbreak News and provides real-time alerts
 * 
 * Ported from EMDA-Kijabe-fresh/src/services/whoData/realTimeOutbreakMonitor.js
 */

class RealTimeOutbreakMonitor {
  constructor() {
    this.activeOutbreaks = new Map();
    this.outbreakHistory = [];
    this.alertSubscribers = new Set();
    this.lastSyncTime = null;
    
    // Countries to monitor closely
    this.priorityCountries = ['IN', 'CN', 'NG', 'BR', 'ID', 'TH', 'AE', 'SG', 'CD', 'AF', 'KE', 'UG'];
    
    // Disease categories for outbreak classification
    this.outbreakCategories = {
      EPIDEMIC: 'Large-scale outbreak requiring immediate attention',
      ENDEMIC_SPIKE: 'Significant increase in endemic disease',
      EMERGING_THREAT: 'New or re-emerging pathogen',
      CROSS_BORDER: 'Multi-country outbreak',
      TRAVEL_RELEVANT: 'Outbreak affecting international travel'
    };
  }

  /**
   * Initialize real-time monitoring with automatic updates
   */
  async startRealTimeMonitoring() {
    
    try {
      await this.syncLatestOutbreaks();
      
      // Set up periodic updates (every 6 hours)
      this.monitoringInterval = setInterval(async () => {
        await this.syncLatestOutbreaks();
      }, 6 * 60 * 60 * 1000);
      
      return true;
      
    } catch (error) {
      console.error('❌ Failed to start outbreak monitoring:', error);
      return false;
    }
  }

  /**
   * Stop real-time monitoring
   */
  stopRealTimeMonitoring() {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = null;
    }
  }

  /**
   * Sync latest outbreak data from WHO and other sources
   */
  async syncLatestOutbreaks() {
    
    try {
      const newOutbreaks = await this.fetchLatestOutbreaks();
      let newOutbreakCount = 0;
      
      for (const outbreak of newOutbreaks) {
        const outbreakKey = `${outbreak.country}_${outbreak.disease}_${outbreak.reportDate}`;
        
        if (!this.activeOutbreaks.has(outbreakKey)) {
          this.activeOutbreaks.set(outbreakKey, outbreak);
          this.outbreakHistory.push(outbreak);
          newOutbreakCount++;
          
          if (this.isHighPriorityOutbreak(outbreak)) {
            await this.sendOutbreakAlert(outbreak);
          }
        }
      }
      
      this.lastSyncTime = new Date().toISOString();
      
      return {
        newOutbreaks: newOutbreakCount,
        totalActive: this.activeOutbreaks.size,
        lastSync: this.lastSyncTime
      };
      
    } catch (error) {
      console.error('❌ Error syncing outbreak data:', error);
      throw error;
    }
  }

  /**
   * Fetch latest outbreak data from multiple sources
   */
  async fetchLatestOutbreaks() {
    // Simulated outbreak data based on real WHO DON structure
    const simulatedOutbreaks = [
      {
        id: 'WHO_DON_2024_001',
        disease: 'dengue',
        country: 'TH',
        region: 'Southeast Asia',
        reportDate: new Date().toISOString().split('T')[0],
        lastUpdate: new Date().toISOString(),
        category: 'ENDEMIC_SPIKE',
        severity: 'moderate',
        casesReported: 15420,
        deathsReported: 23,
        affectedProvinces: ['Bangkok', 'Chiang Mai', 'Phuket'],
        transmissionPattern: 'ongoing_community_transmission',
        riskAssessment: {
          local: 'high',
          regional: 'moderate', 
          international: 'low'
        },
        publicHealthMeasures: ['enhanced_surveillance', 'vector_control'],
        travelRecommendations: 'routine_precautions',
        whoResponse: 'monitoring',
        source: 'WHO Disease Outbreak News'
      },
      {
        id: 'WHO_DON_2024_002',
        disease: 'cholera',
        country: 'CD',
        region: 'Africa',
        reportDate: new Date().toISOString().split('T')[0],
        lastUpdate: new Date().toISOString(),
        category: 'EPIDEMIC',
        severity: 'high',
        casesReported: 2847,
        deathsReported: 89,
        affectedProvinces: ['Kinshasa', 'Kongo Central'],
        transmissionPattern: 'ongoing_community_transmission',
        riskAssessment: {
          local: 'very_high',
          regional: 'high',
          international: 'moderate'
        },
        publicHealthMeasures: ['enhanced_surveillance', 'vaccination_campaign'],
        travelRecommendations: 'enhanced_precautions',
        whoResponse: 'grade_2_emergency',
        source: 'WHO Disease Outbreak News'
      }
    ];

    return simulatedOutbreaks;
  }

  /**
   * Check if outbreak is high priority for immediate alerts
   */
  isHighPriorityOutbreak(outbreak) {
    const highPriorityConditions = [
      outbreak.category === 'EPIDEMIC',
      outbreak.category === 'CROSS_BORDER',
      outbreak.severity === 'high' || outbreak.severity === 'very_high',
      this.priorityCountries.includes(outbreak.country),
      outbreak.riskAssessment?.international === 'high' || outbreak.riskAssessment?.international === 'very_high',
      outbreak.whoResponse === 'grade_2_emergency' || outbreak.whoResponse === 'grade_3_emergency'
    ];

    return highPriorityConditions.some(condition => condition);
  }

  /**
   * Send outbreak alert to subscribers
   */
  async sendOutbreakAlert(outbreak) {
    const alert = {
      type: 'OUTBREAK_ALERT',
      timestamp: new Date().toISOString(),
      priority: this.isHighPriorityOutbreak(outbreak) ? 'high' : 'normal',
      outbreak: outbreak,
      medicalRecommendations: this.generateMedicalRecommendations(outbreak),
      travelGuidance: this.generateTravelGuidance(outbreak)
    };

    for (const subscriber of this.alertSubscribers) {
      try {
        await subscriber(alert);
      } catch (error) {
        console.error('Error notifying outbreak subscriber:', error);
      }
    }

  }

  /**
   * Generate medical recommendations for outbreak
   */
  generateMedicalRecommendations(outbreak) {
    return {
      clinicalVigilance: `Increased vigilance for ${outbreak.disease} in patients with travel history to ${outbreak.country}`,
      diagnosticConsiderations: `Consider ${outbreak.disease} in differential diagnosis for febrile patients from ${outbreak.country}`,
      treatmentGuidelines: `Follow WHO/national guidelines for ${outbreak.disease} management`,
      infectionControl: outbreak.transmissionPattern === 'human_to_human' ? 
        'Implement appropriate isolation precautions' : 
        'Standard precautions sufficient',
      reporting: `Report suspected cases to local health authorities immediately`
    };
  }

  /**
   * Generate travel guidance for outbreak
   */
  generateTravelGuidance(outbreak) {
    return {
      preTravel: {
        riskAssessment: `${outbreak.riskAssessment?.local || 'unknown'} risk in ${outbreak.country}`,
        prophylaxis: this.getTravelProphylaxis(outbreak.disease),
        vaccination: this.getTravelVaccination(outbreak.disease),
        precautions: outbreak.travelRecommendations
      },
      duringTravel: {
        avoidance: this.getTravelAvoidance(outbreak.disease),
        protection: this.getTravelProtection(outbreak.disease)
      },
      postTravel: {
        monitoring: `Monitor for ${outbreak.disease} symptoms for ${this.getMonitoringPeriod(outbreak.disease)}`,
        seekCare: `Seek immediate medical care if symptomatic, mention travel to ${outbreak.country}`
      }
    };
  }

  /**
   * Get current outbreak status for specific country
   */
  getCurrentOutbreaks(countryCode) {
    const countryOutbreaks = [];
    
    for (const [key, outbreak] of this.activeOutbreaks) {
      if (outbreak.country === countryCode) {
        countryOutbreaks.push(outbreak);
      }
    }
    
    return countryOutbreaks.sort(
      (a, b) =>
        new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime()
    );
  }

  /**
   * Get outbreak risk assessment for country
   */
  getCountryRiskAssessment(countryCode) {
    const countryOutbreaks = this.getCurrentOutbreaks(countryCode);
    
    if (countryOutbreaks.length === 0) {
      return {
        overallRisk: 'baseline',
        activeOutbreaks: 0,
        highestPriority: null,
        lastUpdate: this.lastSyncTime
      };
    }

    const highestPriority = countryOutbreaks
      .filter(o => this.isHighPriorityOutbreak(o))[0] || countryOutbreaks[0];

    return {
      overallRisk: highestPriority.riskAssessment?.local || 'moderate',
      activeOutbreaks: countryOutbreaks.length,
      highestPriority,
      outbreakSummary: countryOutbreaks.map(o => ({
        disease: o.disease,
        severity: o.severity,
        cases: o.casesReported,
        lastUpdate: o.lastUpdate
      })),
      lastUpdate: this.lastSyncTime
    };
  }

  /**
   * Subscribe to outbreak alerts
   */
  subscribeToAlerts(callback) {
    this.alertSubscribers.add(callback);
  }

  /**
   * Unsubscribe from outbreak alerts
   */
  unsubscribeFromAlerts(callback) {
    this.alertSubscribers.delete(callback);
  }

  /**
   * Generate outbreak-aware localization enhancement
   */
  generateOutbreakContext(countryCode) {
    const riskAssessment = this.getCountryRiskAssessment(countryCode);
    
    if (riskAssessment.activeOutbreaks === 0) {
      return '';
    }

    let context = `\nCURRENT OUTBREAK CONTEXT:\n`;
    context += `Active Outbreaks: ${riskAssessment.activeOutbreaks}\n`;
    context += `Overall Risk: ${riskAssessment.overallRisk.toUpperCase()}\n`;
    
    if (riskAssessment.highestPriority) {
      const outbreak = riskAssessment.highestPriority;
      context += `Priority Alert: ${outbreak.disease.toUpperCase()} outbreak (${outbreak.casesReported} cases)\n`;
      context += `Clinical Vigilance: Enhanced screening required\n`;
      context += `Last Update: ${outbreak.lastUpdate}\n`;
    }

    return context;
  }

  // Helper methods
  getTravelProphylaxis(disease) {
    const prophylaxis = {
      malaria: 'antimalarial prophylaxis recommended',
      yellow_fever: 'vaccination required',
      dengue: 'no specific prophylaxis - vector avoidance',
      cholera: 'oral cholera vaccine considered for high-risk travelers'
    };
    return prophylaxis[disease] || 'consult travel medicine specialist';
  }

  getTravelVaccination(disease) {
    const vaccines = {
      yellow_fever: 'required for entry',
      cholera: 'recommended for high-risk areas',
      dengue: 'available in select populations',
      typhoid: 'recommended for endemic areas'
    };
    return vaccines[disease] || 'not routinely recommended';
  }

  getTravelAvoidance(disease) {
    const avoidance = {
      dengue: 'avoid mosquito breeding sites',
      cholera: 'avoid contaminated water and food',
      yellow_fever: 'avoid mosquito exposure',
      malaria: 'use bed nets and repellents'
    };
    return avoidance[disease] || 'follow general precautions';
  }

  getTravelProtection(disease) {
    const protection = {
      dengue: 'mosquito repellent, protective clothing',
      cholera: 'safe water, hand hygiene',
      yellow_fever: 'vaccination, mosquito protection',
      malaria: 'prophylaxis, bed nets, repellents'
    };
    return protection[disease] || 'standard precautions';
  }

  getMonitoringPeriod(disease) {
    const periods = {
      dengue: '14 days',
      cholera: '5 days',
      yellow_fever: '6 days',
      malaria: '1 year'
    };
    return periods[disease] || '14 days';
  }
}

export const realTimeOutbreakMonitor = new RealTimeOutbreakMonitor();
export default RealTimeOutbreakMonitor;
